import React, { useEffect, useState } from 'react';
import { Container, Box, Stack, useMediaQuery, Theme, Typography, ButtonProps, Button } from '@mui/material';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet-async';
import PageViewTracker from './PageViewTracker';

const CustomButton = styled(Button)(({ theme }) => ({
  width: '240px',
  height: '56px',
  borderRadius: '4px'
}));

const RegisterSubmitButton: React.FC<ButtonProps> = (props) => {
  return (
    <CustomButton variant="contained" color="primary" {...props}>
      {props.children}
    </CustomButton>
  );
};

const App: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [url, setUrl] = useState<string | null>('');
  const [siteName, setSiteName] = useState<string>('ECサイト');
  const [openButtonState, setOpenButtonState] = useState<number>(0);

  const isSmallScreen = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
  const isSmall = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));

  const sxMargin = isSmall
    ? {
        mt: 5,
        mb: 5,
        ml: 2,
        mr: 2
      }
    : {
        mt: 10,
        mb: 10,
        ml: 3,
        mr: 3
      };

  const LayoutContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  });

  const ContentContainer = styled(Box)({
    flex: '1 0 auto'
  });

  function isiOS26OrLater() {
    try {
      var ua = navigator.userAgent;
      var iOS = /iPhone|iPod/.test(ua);
      var iPadOS = /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (!(iOS || iPadOS)) {
        return false;
      }
      return typeof CSS !== 'undefined' && CSS.supports && CSS.supports('anchor-name', '--a');
    } catch {
      return false;
    }
  }

  function isiOSorIPadOS17Later() {
    try {
      var ua = navigator.userAgent;
      var iOS = /iPhone|iPod/.test(ua);
      var iPadOS = /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (iOS || iPadOS) {
        var versionMatch = ua.match(/OS (\d+)_/) || ua.match(/Version\/(\d+)\./);
        var isVersion17OrHigher = false;
        if (versionMatch && versionMatch.length > 1) {
          var version = parseInt(versionMatch[1], 10);
          isVersion17OrHigher = version >= 17;
        }
        return isVersion17OrHigher;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  }

  function isAndroidWebView() {
    var userAgent = navigator.userAgent || navigator.vendor || window.opera;
    var isWebView = /Version\/4\.0/.test(userAgent);
    var isChrome = /Chrome/.test(userAgent);
    var isAndroid = /Android/.test(userAgent);

    return isAndroid && isWebView && isChrome;
  }

  function isIOSOtherBrowser() {
    try {
      var ua = navigator.userAgent;
      var otherBrowserMatch = /FxiOS/i.test(ua) || /CriOS/i.test(ua);
      if (otherBrowserMatch) {
          return true;
      }
      return false;
    } catch {
        return false;
    }
  }

  function createIntent(url: string) {
    return 'intent://' + url.replace(/^https?:\/\//, '') + 
          '#Intent;scheme=https;end;';
  }

  function getSiteNameFromUrl(url: string): string {
    try {
      const hostname = new URL(url).hostname;
      return hostname;
    } catch {
      return 'ECサイト';
    }
  }

  useEffect(() => {
    var urlParams = new URLSearchParams(location.search);
    var affi_id = urlParams.get('affi_id');
    var url = urlParams.get('site');
    console.log(`url1 = ${url}`)
    // Instagram対策：　2重エンコード自動復元ロジックを追加
    if (url) {
      try {
        // URLの中に '%'が含まれなくなるまで繰り返しデコード
        while (url.includes('%')) {
          const decoded = decodeURIComponent(url);
          if (decoded === url) break; //万が一の無限ループ防止
          url = decoded;
        }
      } catch (e) {
        console.error('Failed to decode site URL:', e);
      }
    }
    console.log(`url2 = ${url}`)
    if (affi_id && url) {
      var separator = url.includes('?') ? '&' : '?';
      url += separator + 'affi_id=' + encodeURIComponent(affi_id);
    }
    if (url) {
      var iosWebView = false;
      var androidWebView = isAndroidWebView();

      if (!iosWebView && !androidWebView) {
        window.location.replace(url);
        setOpenButtonState(0);
      } else {
        if (iosWebView) {
          setOpenButtonState(1);
          setLoading(false);
        } else if (androidWebView) {
          setOpenButtonState(2);
          window.location.replace(createIntent(url!));
          setLoading(false);
        }
      }
      setUrl(url);
      setSiteName(getSiteNameFromUrl(url));
    } else {
      setLoading(false);
    }
  }, []);

  const handleSafariClick = () => {
    window.open('x-safari-' + url, '_blank');
  };
  const handleAndroidClick = () => {
    window.location.replace(createIntent(url!));
  };

  const browserName = () => {
    if (openButtonState === 1) {
      return 'Safari';
    }
    return "ブラウザ";
  }

  const gap = isSmallScreen ? 3 : 5;
  return !loading ? (
    <LayoutContainer
      sx={{

        '& .MuiContainer-root': {
          padding: 0,
        }
      }}
    >
      <PageViewTracker />
      <ContentContainer sx={sxMargin}>
        <Container>
          <Helmet>
            <title>外部サイトを開きます</title>
          </Helmet>

          <Stack direction={'column'} spacing={gap} alignItems={'center'}>
            <Typography variant="h4">外部サイトを開きます</Typography>

            <Stack alignItems="center" spacing={3}>
              {url ? (
                <>
                  <Typography variant="body1">
                    {siteName}を開きます。 <br />以下のボタンから、{browserName()}で開くを選択してください。
                  </Typography>
                  <Stack alignItems="center" spacing={3} mt={5}>
                    { openButtonState === 1 && (
                    <RegisterSubmitButton style={{ textTransform: 'none' }} onClick={handleSafariClick}>
                      Safariで開く
                    </RegisterSubmitButton>
                    )}

                    { openButtonState === 2 && (
                    <RegisterSubmitButton style={{ textTransform: 'none' }} onClick={handleAndroidClick}>
                      ブラウザで開く
                    </RegisterSubmitButton>
                    )}

                  </Stack>
                </>
              ) : (
                <Typography variant="body1">無効なURLです。</Typography>
              )}
            </Stack>
          </Stack>
        </Container>
      </ContentContainer>
    </LayoutContainer>
  ) : (
    <Box />
  );
};

export default App;
